"use client";

import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";

interface Feature13Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType; 
  integrations?: {
    icon?: string;
    image?: string;
    imageAltText?: string;
  }[];
  image?: string;
  imageAltText?: string;
  themeChangeOnScroll?: boolean;
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature13 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 2",
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  integrations,
  image,
  imageAltText,
  themeChangeOnScroll = false,
  attributes,
}: Feature13Props) => {

  const IntegrationElem = ({ image, imageAltText, icon, showConnector = false }: { 
    image?: string; 
    imageAltText?: string; 
    icon?: string;
    showConnector?: boolean;
  }) => (
    <div className="relative flex items-center">
      <div className="relative flex h-16 w-16 rounded-xl border bg-white p-2 md:p-4 shadow-md md:h-20 md:w-20 z-10 items-center justify-center">
        {image && <Image src={image} alt={imageAltText ?? ""} title={imageAltText ?? ""} width={80} height={80} className="object-contain" />}
        {!image && icon && 
          <span className="material-symbols-outlined text-primary m-auto h-10 w-10 !text-[40px]">
            {icon}
          </span>
        }
      </div>
      {showConnector && (
        <>
          {/* Horizontal line */}
          <div className="absolute top-1/2 -right-12 md:-right-16 lg:-right-20 w-12 md:w-16 lg:w-20 h-[2px] bg-border/70 z-[1]" />
          {/* Dot in the middle of the line */}
          <div className="absolute top-1/2 -right-6 md:-right-8 lg:-right-10 w-2 h-2 rounded-full bg-border/70 translate-x-[4px] -translate-y-[3px] z-[1]" />
        </>
      )}
    </div>
  );

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature13"
      attributes={attributes}
    >
      <div className="container">
        <div className="flex flex-col items-center gap-6" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold>
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="text-center">
              {parse(title)}
            </h2>
          )}
          {integrations && integrations.length > 0 && (
            <div className="relative my-4 flex flex-row items-center justify-center gap-12 md:gap-16 lg:gap-20">
              <svg
                className="absolute left-1/2 top-1/2 h-[80%] w-full -translate-x-1/2 -translate-y-1/2"
                viewBox="0 0 1000 50"
                preserveAspectRatio="xMidYMid slice"
              >
                {/* Grid pattern in background */}
                <pattern
                  id="grid"
                  width="18"
                  height="18"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 0 0 L 0 18 M 0 18 L 18 18"
                    fill="none"
                    stroke="oklch(0.872 0.01 258.338)"
                    strokeWidth="0.5"
                  />
                </pattern>
                <rect width="1000" height="50" fill="url(#grid)" opacity="0.7" />
              </svg>

              {integrations?.map((item, index) => (
                <IntegrationElem
                  key={index}
                  image={item.image}
                  imageAltText={item.imageAltText}
                  icon={item.icon}
                  showConnector={index < (integrations?.length ?? 0) - 1}
                />
              ))}
            </div>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground text-center max-w-[660px] mx-auto">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div className="flex flex-col md:flex-row gap-4 mt-4 items-center">
              <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {secondaryButton && (
                <>
                  <span>or</span>
                  <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                    <Link href={secondaryButtonLink ?? ""}>
                      {secondaryButtonText}
                    </Link>
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        
        {image && (
          <div className="mt-20 aspect-[1068/450]  max-w-[1068px] mx-auto" data-aos="fade-up" data-aos-delay="100">
            <Image 
              src={image} 
              alt={imageAltText ?? ""} 
              title={imageAltText ?? ""}
              width={1000} 
              height={1000} 
              className="w-full h-full object-cover rounded-xl" 
              priority
            />
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export { Feature13 };



